import './memo-popup.ajscss';

import { fromMailBasket, MailBasketAction } from '@agent/core/basket';
import { IBasketStore } from '@agent/core/basket/basket.store';
import { RequestModel } from '@agent/core/basket/mail/mail-basket.model';
import { IContactDeprecatedService } from '@agent/core/contact/contact-deprecated.service';
import { RxUtil } from '@commons/util/utilities/rx';
import { IComponentController, IComponentOptions, IQService } from 'angular';
import moment from 'moment';
import { map } from 'rxjs';

import { Memo, MomentService, ProfileService, UntilDestroy, untilDestroyed } from '@contact/upgrade';

import { IEpticaDeprecatedService } from '../../eptica/eptica-deprecated.service';
import templateUrl from './memo-popup.html';
import { MemoPopUpActionEnum } from './models/memo-popup-enums';

const DEFER_TYPES = {
  WITHOUT_REPLY: false,
  WITH_REPLY: true,
};

@UntilDestroy()
class MemoPopupCtrl implements IComponentController {
  close: Function;
  resolve: Function;

  responseId: string;
  isRecipientListValid: boolean;
  disableAction: boolean;
  mailMemo: Memo;
  // awlDifferMemo set in eptica_server ( 4096 bits / 8 to get bytes / 2 to get nbChars)
  awlDifferMemoSize: number = 4096 / 16;
  // offset to store the value : start with : '{"message":"' and end with '","date":"1613725130644"}' so 40 chars for futur date
  differMemoOffset: number = 40;
  // actual max size for comment
  differCommentMaxSize: number = this.awlDifferMemoSize - this.differMemoOffset;
  actionButtonText: string;
  titleText: string;
  placeholder: string;
  public deferType = !this.profileService.hasPermission('CSR_MAIL_INCOMING::contact:mail_defer');
  public bookmark = false;
  private epticaRequestId: string;

  constructor(
    private readonly $q: IQService,
    private readonly contactDeprecatedService: IContactDeprecatedService,
    private readonly epticaDeprecatedService: IEpticaDeprecatedService,
    private profileService: ProfileService,
    private readonly timeUtil: MomentService,
    private basketStore: IBasketStore
  ) {}

  $onInit(): void {
    this.responseId = this.resolve().responseId;
    this.disableAction = this.resolve().disableAction;
    this.isRecipientListValid = this.resolve().isRecipientListValid;

    const action = this.resolve().action ? this.resolve().action : MemoPopUpActionEnum.DEFER;
    this.setTitles(action);

    this.contactDeprecatedService.getContact(this.responseId).then(contact => {
      const { epticaRequestId } = contact;
      this.epticaRequestId = epticaRequestId ? epticaRequestId.toString() : '';
      let request$ = this.basketStore.pipe(fromMailBasket.selectCurrentRequests<RequestModel>());
      request$
        .pipe(
          map(items => items.find(el => el.id === this.epticaRequestId)),
          untilDestroyed(this)
        )
        .subscribe(requests => (this.bookmark = requests ? requests.bookmark : false));
      this.mailMemo = {
        message: contact.mailMemo?.message ?? '',
        date: this.timeUtil.readableDateTime(moment().toDate()),
      };
    });
  }

  /**
   * Defer the mail (submit eptica's form)
   */
  defer(): void {
    if (this.deferType === DEFER_TYPES.WITH_REPLY) {
      if (!this.mailMemo || !this.mailMemo.hasOwnProperty('message')) {
        this.mailMemo = {
          date: '',
          message: '',
        };
      }

      if (this.epticaRequestId) {
        RxUtil.toPromise(this.epticaDeprecatedService.deferRequest(this.epticaRequestId, this.mailMemo, this.bookmark), this.$q).then(
          () => {
            this.resolve().deferAction();
            this.close();
          }
        );
      }
    } else {
      this.contactDeprecatedService
        .refuse(this.responseId, 'DEFER')
        .then(() => {
          RxUtil.toPromise(this.epticaDeprecatedService.deferRequest(this.epticaRequestId, this.mailMemo, this.bookmark), this.$q);
        })
        .then(() => {
          const change: Partial<RequestModel> = { bookmark: this.bookmark, memo: this.mailMemo };
          this.basketStore.dispatch(new MailBasketAction.ChangeBookmark(this.epticaRequestId, this.bookmark));
          this.basketStore.dispatch(new MailBasketAction.UpdateRequest(this.epticaRequestId, change));
        })
        .then(() => {
          this.resolve().deferWithoutSendAction();
          this.close();
        });
    }
  }

  setTitles(action: string): void {
    if (action === MemoPopUpActionEnum.GIVE_UP) {
      this.titleText = this.actionButtonText = 'agent:contact.workspace.basket.give_up';
      this.placeholder = 'agent:contact.workspace.basket.comment_placeholder';
    } else {
      this.actionButtonText = 'agent:actions.defer';
      this.titleText = 'agent:popin.create_memo.title';
      this.placeholder = 'agent:popin.create_memo.placeholder';
    }
  }

  cancel(): void {
    this.close();
  }
}

export class MemoPopup implements IComponentOptions {
  templateUrl = templateUrl;
  bindings = {
    // from ui-bootstrap modal
    resolve: '&',
    close: '&',
  };
  controller = [
    '$q',
    'contactDeprecatedService',
    'epticaDeprecatedService',
    'profileService',
    'momentService',
    'basketStore',
    MemoPopupCtrl,
  ];
}

export default 'memoPopup';
